import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token storage helpers
const TOKEN_KEY = "impress360_session_token";

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function storeToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Set up axios interceptor for Authorization header
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.log("[AuthContext] checkAuth failed, clearing token");
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/callback') {
      console.log("[AuthContext] Skipping checkAuth - OAuth callback detected");
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const handleOAuthCallback = async (sessionId) => {
    try {
      console.log("[AuthContext] Exchanging session_id, length:", sessionId?.length);
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId }
      );
      console.log("[AuthContext] Session exchange success:", response.data?.email);
      
      // Store the session token for Authorization header usage
      if (response.data.session_token) {
        storeToken(response.data.session_token);
      }
      
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("[AuthContext] Session exchange failed:", error.response?.status, error.response?.data);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearToken();
      setUser(null);
    }
  };

  const getGoogleAuthUrl = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        isSuperAdmin: user?.role === "super_admin",
        handleOAuthCallback,
        logout,
        getGoogleAuthUrl,
        refreshUser: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
